import { CheckCircle2, CircleAlert, CircleDashed, CircleX, Hourglass, RotateCcw, type LucideIcon } from 'lucide-react'
import { formatEventTime } from '@/lib/utils'
import type { ActionAuditEvent } from './action-audit-model'

const EVENT_STYLE: Record<
  ActionAuditEvent['kind'],
  { label: string; icon: LucideIcon; iconClass: string }
> = {
  raised: { label: 'Raised', icon: CircleAlert, iconClass: 'text-amber-600' },
  resolved: { label: 'Resolved', icon: CheckCircle2, iconClass: 'text-green-600' },
  dismissed: { label: 'Dismissed', icon: CircleX, iconClass: 'text-muted-foreground' },
  reopened: { label: 'Reopened', icon: RotateCcw, iconClass: 'text-blue-600' },
  started: { label: 'In progress', icon: Hourglass, iconClass: 'text-sky-600' },
  stopped: { label: 'Marked not started', icon: CircleDashed, iconClass: 'text-muted-foreground' },
}

/** "Dismissed by Abbey Coleman · Sep 14, 9:12 AM" with a status icon. */
export function ActionAuditLine({ event, subject }: { event: ActionAuditEvent; subject?: string }) {
  const style = EVENT_STYLE[event.kind]
  const label = subject ? `${subject} ${style.label.toLowerCase()}` : style.label
  const Icon = style.icon
  const line = (
    <span className="inline-flex items-center gap-1.5">
      <Icon className={`h-3.5 w-3.5 shrink-0 ${style.iconClass}`} aria-hidden />
      <span>
        <span className="font-medium text-foreground">{label}</span>
        {event.actor && <> by {event.actor}</>}
        <span aria-hidden> · </span>
        <time dateTime={event.at}>{formatEventTime(event.at)}</time>
      </span>
    </span>
  )
  if (!event.note) return line
  // A note left with an earlier dismissal takes its own row under that event.
  return (
    <span className="flex basis-full flex-col gap-0.5">
      {line}
      <span className="whitespace-pre-line break-words pl-5 text-foreground/80">{event.note}</span>
    </span>
  )
}
